/* eslint-disable no-underscore-dangle */
import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import { Link, Switch } from 'react-router-dom';
import { push } from 'react-router-redux';
import moment from 'moment';
import { path } from 'ramda';

import SearchSource from '../../../../src/sources/search';
import FSFlatButton from '../../../../src/components/lib/FSFlatButton';
import componentBase from '../../../../src/lib/component-base';
import { errorMessageForCode } from '../../../../src/lib/errors';
import flexErrors from '../../../../src/constants/flex-errors';
import { Colors } from '../../../../src/constants/style-constants';
import {
  setRandomFlightPath,
  setFlightTrackerSearchError,
  setUniversalSearchFlight,
  setAdvancedSearchUrl,
} from '../../redux/Search/actions';
import {
  setSingleFlightSecondarySearchAction,
} from '../../../SingleFlight/redux/actions';
import Ad from '../../../../shared/components/Ad/Ad';
import BannerAd from '../../../../shared/components/BannerAd/BannerAd';
import { AdvancedSearch as advancedSearchAds, UnifiedSearch as unifiedSearchAds } from '../ads';
import { addMetaCanonical } from '../../../../shared/lib/meta-tags';
import RouteWrapper from '../../../RouteWrapper';
import AdvancedSearch from './children/AdvancedSearch/AdvancedSearch';
import FlightSearch from '../../../FlightSearch/components/container';

@connect(state => ({
  randomFlightPath: state.FlightTrackerSearch.randomFlightPath,
  airline: state.FlightTrackerSearch.airline,
  flightNumber: state.FlightTrackerSearch.flightNumber,
  departureAirport: state.FlightTrackerSearch.departureAirport,
  arrivalAirport: state.FlightTrackerSearch.arrivalAirport,
  error: state.FlightTrackerSearch.error,
  departureDate: state.FlightTrackerSearch.departureDate,
  departureTime: state.FlightTrackerSearch.departureTime,
  universalSearchFlight: state.FlightTrackerSearch.universalSearchFlight,
  universalSearchValue: state.FlightTrackerSearch.universalSearchValue,
  advancedSearchUrl: state.FlightTrackerSearch.advancedSearchUrl,
  queryError: state.FlightTrackerSearch.queryError,
  secondarySearchAction: state.FlightTrackerSearch.secondarySearchAction,
  secondaryAdvancedSearchAction: state.FlightTrackerSearch.secondaryAdvancedSearchAction,
  secondaryAdvancedSearchActionAirline:
    state.FlightTrackerSearch.secondaryAdvancedSearchActionAirline,
  secondaryAdvancedSearchActionDepAirport:
    state.FlightTrackerSearch.secondaryAdvancedSearchActionDepAirport,
  secondaryAdvancedSearchActionArrAirport:
    state.FlightTrackerSearch.secondaryAdvancedSearchActionArrAirport,
  secondaryAdvancedSearchActionDepDate:
    state.FlightTrackerSearch.secondaryAdvancedSearchActionDepDate,
  secondaryAdvancedSearchActionTime:
    state.FlightTrackerSearch.secondaryAdvancedSearchActionTime,
  changingFlightNumberInput: state.FlightTrackerSearch.changingFlightNumberInput,
}))
@componentBase('FlightTrackerSearch')
export default class FlightTrackerSearch extends React.Component {
  static propTypes = {
    location: PropTypes.object,
    children: PropTypes.object,
    randomFlightPath: PropTypes.string,
    dispatch: PropTypes.func,
    airline: PropTypes.object,
    departureAirport: PropTypes.object,
    arrivalAirport: PropTypes.object,
    flightNumber: PropTypes.string,
    error: PropTypes.string,
    departureDate: PropTypes.string,
    departureTime: PropTypes.number,
    universalSearchFlight: PropTypes.object,
    universalSearchValue: PropTypes.object,
    advancedSearchUrl: PropTypes.string,
    queryError: PropTypes.string,
    secondarySearchAction: PropTypes.bool,
    secondaryAdvancedSearchAction: PropTypes.bool,
    secondaryAdvancedSearchActionAirline: PropTypes.bool,
    secondaryAdvancedSearchActionDepAirport: PropTypes.bool,
    secondaryAdvancedSearchActionArrAirport: PropTypes.bool,
    secondaryAdvancedSearchActionDepDate: PropTypes.bool,
    secondaryAdvancedSearchActionTime: PropTypes.bool,
    history: PropTypes.object,
    changingFlightNumberInput: PropTypes.bool,
  };

  constructor(props, context) {
    super(props, context);

    this.submit = this.submit.bind(this);
    this.reportButtonPress = this.reportButtonPress.bind(this);
  }

  componentDidMount() {
    this.updateRandomFlightPath();
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    // clear error message
    dispatch(setFlightTrackerSearchError());
  }

  getCodes = (airLinePort) => {
    if (airLinePort) {
      const { value } = airLinePort;
      const { _source } = value;
      return (_source && (_source.fs || _source.iata || _source.icao)) || null;
    }
    return null;
  }

  backToBasicSearchClick = () => {
    this.reportButtonPress('Back to Basic Search');
    this.props.dispatch(setFlightTrackerSearchError());
  }

  advancedSearchClick = () => {
    this.reportButtonPress('Go to Advanced Search');
    this.props.dispatch(setFlightTrackerSearchError());
  }

  buildUrl = (props) => {
    const {
      airline,
      flightNumber,
      departureAirport,
      arrivalAirport,
      departureDate,
      departureTime,
    } = props;

    let urlPath = null;

    const airlineCode = this.getCodes(airline);
    const departureAirportCode = this.getCodes(departureAirport);
    const arrivalAirportCode = this.getCodes(arrivalAirport);

    if (flightNumber && airlineCode) {
      urlPath = `/flight-tracker/${airlineCode}/${flightNumber}/${departureDate}`;
    } else if (departureAirportCode && !arrivalAirportCode) {
      urlPath = `/flight-tracker/departing/${departureAirportCode}/${departureDate}/${departureTime}${airlineCode ? `/${airlineCode}` : ''}`;
    } else if (arrivalAirportCode && !departureAirportCode) {
      urlPath = `/flight-tracker/arriving/${arrivalAirportCode}/${departureDate}/${departureTime}${airlineCode ? `/${airlineCode}` : ''}`;
    } else if (departureAirportCode && arrivalAirportCode) {
      urlPath = `/flight-tracker/route/${departureAirportCode}/${arrivalAirportCode}/${departureDate}/${departureTime}${airlineCode ? `/${airlineCode}` : ''}`;
    }

    return urlPath;
  }

  validate = (isUniversalSearch) => {
    if (isUniversalSearch) {
      const { universalSearchValue } = this.props;

      if (!universalSearchValue) { return 'UNIVERSAL_SEARCH_EMPTY'; }

      return null;
    }

    const {
      airline, flightNumber, departureAirport, arrivalAirport,
    } = this.props;

    let errorCode = null;
    // If there is no airline
    if (!airline) {
      // Check if the user entered a flight number that matches the regex
      if (flightNumber) {
        if (airline === undefined) {
          errorCode = 'BAD_AIRLINE_CODE';
        } else {
          errorCode = 'MISSING_AIRLINE_CODE';
        }
      } else if (!flightNumber && !departureAirport && !arrivalAirport) errorCode = 'NO_INFORMATION_ENTERED';
    } else if (!flightNumber && !arrivalAirport && !departureAirport) errorCode = 'MISSING_FLIGHT_NUMBER_OR_ROUTE';
    // There is a airline present

    return errorCode;
  };

  updateRandomFlightPath = () => {
    SearchSource.randomFlight(this.context.store.getState().Account.serverTime)
      .then((flight) => {
        if (!flight || !flight.data[0] || !flight.data[0]._source) {
          throw new Error('Could not get random flight.');
        }
        const source = flight.data[0]._source;
        const ddt = moment(source.departureDateTime);
        const randomPath = `/flight-tracker/${source.carrier}/${source.flightNumber}/${ddt.year()}/${ddt.month() + 1}/${ddt.date()}/${source.flightId}`;
        this.props.dispatch(setRandomFlightPath(randomPath));
      })
      .catch((err) => {
        console.log('Error updating random flight:', err);
      });
  }

  isUniversalSearchMode = () => this.props.location.pathname.indexOf('advanced') === -1;

  submit(e, url) {
    e && e.preventDefault && e.preventDefault();
    const {
      advancedSearchUrl,
      dispatch,
      history,
      universalSearchValue,
    } = this.props;

    const isUniversalSearch = this.isUniversalSearchMode();
    const errorCode = this.validate(isUniversalSearch);

    dispatch(setFlightTrackerSearchError(errorCode));

    const destination = (isUniversalSearch ?
      universalSearchValue && universalSearchValue.url : advancedSearchUrl) ||
      url ||
      this.buildUrl(this.props);

    if (errorCode == null) {
      dispatch(setUniversalSearchFlight({
        flight: '',
        label: '',
        url: '',
      }));

      // reset search triggers
      dispatch(setSingleFlightSecondarySearchAction(true));
      dispatch(setAdvancedSearchUrl(''));
      const topOfStackHistory = path(['location', 'pathname'], history);
      if (destination && topOfStackHistory !== destination) {
        // multiple identical entries were being pushed into history on enter keydown
        dispatch(push(destination));
      }
    }
  }

  metaTags = () => {
    const title = 'Flight Tracker';
    const description = 'Global Flight Tracker. Track real-time flight status to see if your flight has been delayed or cancelled and track the flight\'s current position on a map.';
    const metaWithoutCanonical = {
      title,
      meta: [
        { name: 'description', content: description },
        { name: 'keywords', content: 'flight tracker, live flight tracking, track flight, flight tracker live, flight tracking' },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
      ],
    };
    return addMetaCanonical(metaWithoutCanonical, '/flight-tracker/search');
  }

  secondarySearchActions = () => {
    this.submit();
  }

  reportButtonPress = (label) => {
    this.context.reportButtonPress(label);
  }

  render = () => {
    const isUniversalSearchMode = this.isUniversalSearchMode();

    const universalSearchStyle = {
      display: isUniversalSearchMode ? 'block' : 'none',
    };

    const advancedSearchStyle = {
      display: isUniversalSearchMode ? 'none' : 'block',
    };

    let error = '';
    const queryError = this.props.queryError || '';

    if (this.props.error) {
      error = (
        <div className='search-error-text-block'>
          <p className='search-error-text'>{flexErrors(this.props.error)}</p>
        </div>
      );
    } else if (queryError) {
      error = (
        <div className='search-error-text-block'>
          <p className='search-error-text'>{errorMessageForCode(queryError)}</p>
        </div>
      );
    }

    const meta = this.metaTags();
    const subscriptionActive = (
      this.context &&
      this.context.currentUser &&
      this.context.currentUser.subscriptionActive
    );

    const adUnits = {
      mobileAd: unifiedSearchAds.topMobile,
      desktopAd: unifiedSearchAds.top,
    };
    let boxAdUnit = unifiedSearchAds.bottom;

    if (!isUniversalSearchMode) {
      adUnits.mobileAd = advancedSearchAds.topMobile;
      adUnits.desktopAd = advancedSearchAds.top;
      boxAdUnit = advancedSearchAds.bottom;
    }

    const topAd = (
      <BannerAd
        topAd
        needSpace
        {...adUnits}
        subscriptionActive={subscriptionActive}
      />
    );

    const fsSearchAdStyle = subscriptionActive ? {} : { paddingBottom: '0' };

    const boxAd = subscriptionActive ? null : (
      <center style={{ margin: `${isUniversalSearchMode ? '50px' : '25px'} 0 25px` }}>
        <Ad
          key={boxAdUnit.id}
          {...boxAdUnit}
          subscriptionActive={subscriptionActive}
        />
      </center>
    );

    return (
      <section>
        <Helmet {...meta} />
        <div style={{ top: 0, margin: '0 auto', width: '100%' }}>
          {topAd}
          <div className='fs-search' style={fsSearchAdStyle}>
            <div style={advancedSearchStyle}>
              <div className='row' style={{ marginBottom: '10px' }}>
                <h2 style={{ display: 'inline-block', marginBottom: '10px' }}>Flight Tracker</h2>
                <Link to='/flight-tracker/search'>
                  <FSFlatButton
                    label='&lt; Back to Basic Search'
                    style={FSFlatButton.style({ float: 'right', width: '200px', marginBottom: '10px' })}
                    onClick={this.backToBasicSearchClick}
                    backgroundColor={Colors.blue6}
                    hoverColor={Colors.blue8}
                    secondary
                  />
                </Link>
                {error}
              </div>
              <p style={{ marginTop: '7px', fontSize: '11px' }}>Please provide, at a minimum, an airline and a flight number and either a departure or an arrival airport.</p>
            </div>
            <div style={universalSearchStyle}>
              <h2 style={{ display: 'inline-block', marginBottom: '10px' }}>Flight Tracker</h2>
            </div>
            <Switch>
              <RouteWrapper
                path='/flight-tracker/search'
                name='UnifiedSearch'
                exact
                component={FlightSearch}
                secondarySearchAction={this.props.secondarySearchAction}
              />
              <RouteWrapper
                path='/flight-tracker/search/advanced'
                name='AdvancedSearch'
                exact
                component={AdvancedSearch}
                buildUrl={this.buildUrl}
                arrivalAirport={this.props.arrivalAirport}
                airline={this.props.airline}
                departureAirport={this.props.departureAirport}
                departureDate={this.props.departureDate}
                departureTime={this.props.departureTime}
                flightNumber={this.props.flightNumber}
                changingFlightNumberInput={this.props.changingFlightNumberInput}
                secondaryAdvancedSearchActionAirline={
                  this.props.secondaryAdvancedSearchActionAirline
                }
                secondaryAdvancedSearchActionDepAirport={
                  this.props.secondaryAdvancedSearchActionDepAirport
                }
                secondaryAdvancedSearchActionArrAirport={
                  this.props.secondaryAdvancedSearchActionArrAirport
                }
                secondaryAdvancedSearchActionDepDate={
                  this.props.secondaryAdvancedSearchActionDepDate
                }
                secondaryAdvancedSearchActionTime={
                  this.props.secondaryAdvancedSearchActionTime
                }
                submit={this.submit}
              />
            </Switch>
            <div style={advancedSearchStyle}>
              <div style={{ textAlign: 'center', paddingBottom: '20px', borderBottom: '2px solid #ccc' }}>
                <div className='fs-advanced-search-button'>
                  <FSFlatButton
                    label='Search'
                    type='submit'
                    style={FSFlatButton.style({ width: '200px' })}
                    onClick={(e) => {
                      this.reportButtonPress('Advanced Search');
                      this.submit(e);
                    }}
                    primary
                  />
                </div>
              </div>
              <div style={{ padding: '15px', textAlign: 'center' }}>
                <h5 style={{ fontSize: '20px', display: 'inline-block', marginRight: '7px' }}>Do You Want to Track a Random Flight?</h5>
                <Link
                  to={this.props.randomFlightPath || ''}
                  onClick={() => { this.context.reportButtonPress('Random Flight'); }}
                >
                  <h5 id='random-flight-link' style={{ fontSize: '20px', display: 'inline-block', textDecoration: 'underline' }}>Click Here</h5>
                </Link>
              </div>
            </div>
          </div>
          {boxAd}
        </div>
      </section>
    );
  }
}
/* eslint-enable no-underscore-dangle */



// WEBPACK FOOTER //
// ./modules/FlightTracker/components/Search/FlightTrackerSearch.js